/**
 * Type definitions for the Onboarding Modal
 */
import {
  AdGroupResponse,
  ApplicationResponse,
  cnapResponse,
  GithubRepo,
} from '@devxdiscover/devhub-common';

export type SignalType<T> = {
  event: string;
  status: 'pending' | 'in-progress' | 'completed' | 'failed' | 'skipped';
  taskId: string;
  progress?: number;
  data: T;
  error?: string;
};

export interface AppCreationSignal extends SignalType<ApplicationResponse> {
  event: 'app-creation';
}

export interface GitHubRepoSignal extends SignalType<GithubRepo> {
  event: 'github-repo';
}

export interface cnapTenancySignal extends SignalType<cnapResponse> {
  event: 'cnap-tenancy';
}

export interface AdGroupSignal extends SignalType<AdGroupResponse> {
  event: 'ad-groups';
}

export interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (createdApp?: {
    applicationId: number;
    name: string;
    description: string;
    ownerLogin: string;
  }) => void;
  onViewIdeas?: () => void;
}

export interface OnboardingFormData {
  name: string;
  description: string;
  identifier: string;
}

export interface TouchedFields {
  name: boolean;
  description: boolean;
  identifier: boolean;
}

export interface ProvisioningComponent {
  id: string;
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'failed' | 'skipped';
  progress: number;
  error?: string;
}

export interface CreatedAppData {
  applicationId: number;
  name: string;
  description: string;
  ownerLogin: string;
}

/**
 * Type guard to check if a signal is an app creation signal
 * @param signal - The signal to check
 * @returns True if signal is AppCreationSignal
 */
export function isAppCreationSignal(signal: any): signal is AppCreationSignal {
  return signal?.event === 'app-creation';
}

/**
 * Type guard to check if a signal is a GitHub repo signal
 * @param signal - The signal to check
 * @returns True if signal is GitHubRepoSignal
 */
export function isGitHubRepoSignal(signal: any): signal is GitHubRepoSignal {
  return signal?.event === 'github-repo';
}

/**
 * Type guard to check if a signal is a CNAP tenancy signal
 * @param signal - The signal to check
 * @returns True if signal is cnapTenancySignal
 */
export function isCnapTenancySignal(signal: any): signal is cnapTenancySignal {
  return signal?.event === 'cnap-tenancy';
}

/**
 * Type guard to check if a signal is an AD group signal
 * @param signal - The signal to check
 * @returns True if signal is AdGroupSignal
 */
export function isAdGroupSignal(signal: any): signal is AdGroupSignal {
  return signal?.event === 'ad-groups';
}
